import axios, { AxiosError, AxiosInstance } from 'axios';

import { refreshToken as refreshAccessToken } from '../authApi/authApi';

const api: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_API_URL}`,
});

const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem('refresh_token');
};

const FIRM_USER_DELETED = 'FIRM_USER_DELETED';

const isFirmUserDeletedError = (error: unknown): boolean => {
  const data = (error as AxiosError<{ code?: string }>)?.response?.data;

  return data?.code === FIRM_USER_DELETED;
};

const logout = (): void => {
  localStorage.clear();
  sessionStorage.clear();

  window.location.href = '/sign-in';
};

const getNewAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();

  if (!refreshToken || !accessToken) {
    throw new Error('No tokens available');
  }

  // Errors are not wrapped so the response interceptor can detect FIRM_USER_DELETED
  const { data } = await refreshAccessToken({ refreshToken, accessToken });

  localStorage.setItem('access_token', data.accessToken);

  return data.accessToken;
};

api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();

    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { response, config } = error;

    // Deactivated user: force logout, handled separately from refresh-token failures
    if (isFirmUserDeletedError(error)) {
      logout();

      return Promise.reject(error);
    }

    if (config && response?.status === 401) {
      try {
        const newAccessToken = await getNewAccessToken();

        if (newAccessToken) {
          config.headers.set('Authorization', `Bearer ${newAccessToken}`);

          return api(config);
        }
      } catch (refreshError) {
        if (!isFirmUserDeletedError(refreshError)) {
          console.error('Unable to refresh token:', refreshError);
        }
        logout();
      }
    }

    return Promise.reject(error);
  },
);

export default api;
